import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import MobileNav from "@/components/MobileNav";
import { GoogleCalendarMonth, CalendarWeekView } from "@/components/experiences/CalendarViews";
import { getCategoryPreset, CATEGORY_ORDER } from "@/lib/categoryConfig";
import { getPreferenceSchema } from "@/lib/preferenceSchemas";
import { PLAN_CTAS } from "@/lib/planCtaCopy";
import {
  ArrowLeft,
  Plus,
  Calendar,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Clock,
  X,
  CalendarDays,
  LayoutGrid,
  ListChecks,
  ArrowUp,
  UserCheck,
  Info,
  ExternalLink,
  Eye,
  Upload,
  Pencil,
  Trash2,
  Link2,
  Unlink,
  Check,
  Wand2,
  Sparkles,
  Heart,
  Camera,
  Palette,
  Image as ImageIcon,
  PartyPopper,
  Cake,
  Plane,
  Star,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  BookOpen,
  Globe,
  Instagram,
  GripVertical
} from "lucide-react";
import { toast } from "sonner";
import { PlanExperienceModal, PlanEventModal } from "@/components/experiences/PlanModals";

/* ── Constants ── */
const OCCASION_OPTIONS = [
  { id: "just_planning", label: "Just Planning", icon: Sparkles },
  { id: "maintenance", label: "Regular Maintenance", icon: RefreshCw },
  { id: "special_event", label: "Special Event", icon: PartyPopper },
  { id: "wedding", label: "Wedding / Bridal", icon: Heart },
  { id: "birthday", label: "Birthday", icon: Cake },
  { id: "date_night", label: "Date Night", icon: Heart },
  { id: "vacation", label: "Vacation", icon: Plane },
  { id: "new_look", label: "New Look", icon: Star },
];

// Iter 9.40 Pass I.2 - Categories that are LIVE in Launch Control show normally.
// Waitlist categories show with "Waitlist" badge. Hidden are excluded entirely.
// Default fallback (if API fails) allows the user's city category.
const DEFAULT_LIVE_CATEGORIES = ["makeup", "hair", "lashes", "events", "instructors"];

// Category → which default blocks are relevant.
// R7: Hair has NO Face. Each category is scoped to its own service-relevant blocks.
const CATEGORY_DEFAULTS_MAP = {
  hair: ["hair"],
  makeup: ["face", "eyes_lips"],
  face: ["face"],
  nails: ["nails"],
  lash: ["lashes"],
  lashes: ["lashes"],
  culinary: ["dietary"],
  events: ["events"],
  wellness: ["wellness"],
  travel: ["travel"],
  retail: ["style"],
  dispensary: ["dispensary"],
  instructors: ["learning"],
};

// Empty field shapes per block, used to render the inline form when user
// has no saved prefs yet (prevents the "fill on Defaults tab first" dead-end)
const DEFAULT_BLOCK_FIELDS = {
  hair: { hair_type: "", hair_texture: "", length: "", style: "", concerns: [], notes: "" },
  face: { skin_type: "", skin_concerns: [], allergies: [], notes: "" },
  nails: { shape: "", length: "", finish: "", color_preference: "", concerns: [], notes: "" },
  lash: { style: "", length: "", curl: "", concerns: [], notes: "" },
  dietary: { restrictions: [], allergies: [], preferences: [], notes: "" },
  accessibility: { mobility: "", vision: "", hearing: "", other: [], notes: "" },
  interests: { topics: [], hobbies: [], favorites: [], notes: "" },
  wellness: { focus_areas: [], pressure: "", concerns: [], notes: "" },
  travel: { destinations: [], style: "", pace: "", notes: "" },
};

// Event templates fallback (replaced by API data when available)
const EVENT_TEMPLATES_FALLBACK = {
  wedding: { label: "Wedding", services: ["hair", "makeup", "nails", "lashes"] },
  birthday: { label: "Birthday", services: ["makeup", "hair"] },
  photoshoot: { label: "Photoshoot", services: ["makeup", "hair"] },
  girls_night: { label: "Girls Night", services: ["makeup", "nails"] },
  prom: { label: "Prom / Formal", services: ["hair", "makeup", "nails", "lashes"] },
  date_night: { label: "Date Night", services: ["makeup", "hair"] },
};

/* ── Lightbox ── */
const InspoLightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex || 0);
  if (!images?.length) return null;
  const cur = images[idx];
  const url = typeof cur === "string" ? cur : (cur?.image_url || "");
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="inspo-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white z-10"><X className="w-7 h-7" /></button>
      {images.length > 1 && (<>
        <button onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 rounded-full p-2 z-10"><ChevronLeft className="w-6 h-6 text-white" /></button>
        <button onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 rounded-full p-2 z-10"><ChevronRight className="w-6 h-6 text-white" /></button>
      </>)}
      {url && <img src={url} alt="" className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />}
      <div className="absolute bottom-4 text-white/70 text-sm">{idx + 1} / {images.length}</div>
    </div>
  );
};
/* ── Provider Display Helper ── */
const formatProvider = (name) => {
  if (!name) return null;
  const lower = name.toLowerCase();
  if (lower === "self-service" || lower === "self" || lower === "me" || lower === "diy") return "DIY (me!)";
  return name;
};

/* ── Human-readable field labels (admin-synced display names) ── */
const FIELD_LABELS = {
  // Accessibility
  accessibility_needs: "Accessibility Needs", physical_needs: "Physical Needs",
  invisible_disabilities: "Invisible Disabilities", notes: "Additional Notes",
  fragrance_free: "Fragrance-Free Required", sensory_needs: "Sensory Needs",
  service_animal: "Service Animal",
  // Hair
  hair_type: "Hair Type", hair_texture: "Texture", hair_length: "Length",
  hair_concerns: "Concerns", color_history: "Color History", heat_tolerance: "Heat Tolerance",
  natural_color: "Natural Color", chemical_treatments: "Chemical Treatments",
  // Face / Skin
  skin_tone: "Skin Tone", skin_type: "Skin Type", undertone: "Undertone",
  coverage: "Coverage", eye_shape: "Eye Shape", allergies: "Allergies",
  sensitivities: "Sensitivities", preferred_scents: "Preferred Scents",
  foundation_shade: "Foundation Shade",
  // Nails
  nail_shape: "Nail Shape", nail_length: "Nail Length", gel_or_acrylic: "Gel or Acrylic",
  nail_conditions: "Nail Conditions", lifestyle: "Lifestyle",
  // Lash
  lash_type: "Lash Style", curl_preference: "Curl Preference", length: "Length",
  material: "Material", lash_concerns: "Lash Concerns",
  strip_type: "Strip Type", band_type: "Band Type", adhesive_sensitivity: "Adhesive Sensitivity",
  // Dietary
  restrictions: "Dietary Restrictions", dietary_restrictions: "Dietary Restrictions",
  dietary_notes: "Dietary Notes", meat_preferences: "Meat Preferences",
  intolerances: "Intolerances",
  // Wellness
  medical_history: "Medical History", injuries: "Injuries/Limitations",
  medications: "Current Medications", stress_level: "Stress Level",
  sleep_quality: "Sleep Quality", fitness_level: "Fitness Level",
  // Travel
  passport_status: "Passport Status", seat_preference: "Seat Preference",
  hotel_type: "Hotel Type", travel_style: "Travel Style",
  // Interests
  interests: "Interests", style_preferences: "Style Preferences",
  learning_goals: "Learning Goals", conversation_mood: "Conversation Mood",
  vibe: "Vibe", vibe_preferences: "Vibe Preferences",
  seating_preference: "Seating Preference",
  // General
  filled: null, // hide this key
};

/* ── Humanize values (snake_case → "Title Case") ── */
const humanizeValue = (val) => {
  if (val === null || val === undefined) return "";
  if (Array.isArray(val)) return val.map(v => humanizeValue(v)).join(", ");
  if (typeof val === "boolean") return val ? "Yes" : "No";
  const s = String(val);
  // Convert snake_case to Title Case
  return s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

/* ── Get label for a field key ── */
const getFieldLabel = (key) => {
  if (FIELD_LABELS[key] === null) return null; // explicitly hidden
  return FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};


/* ── Inspo Row ── */
const InspoRow = ({ inspo, onOpen }) => {
  if (!inspo?.length) return null;
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="exp-inspo-row">
      {inspo.slice(0, 4).map((img, i) => {
        const url = typeof img === "string" ? img : (img?.image_url || "");
        return url ? (
          <button key={i} onClick={() => onOpen(inspo, i)} className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200 hover:ring-2 hover:ring-purple-300 transition-all cursor-zoom-in">
            <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
          </button>
        ) : null;
      })}
      {inspo.length > 4 && (
        <button onClick={() => onOpen(inspo, 4)} className="w-12 h-12 rounded-lg flex-shrink-0 bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-500 font-medium hover:bg-gray-200 cursor-zoom-in">+{inspo.length - 4}</button>
      )}
    </div>
  );
};

/* ── Main Page ── */
const ExperiencesPage = ({ embedded = false }) => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("calendar");
  const [categoryFilter, setCategoryFilter] = useState("all"); // "all" | category id (e.g. "hair")
  const [expViewMode, setExpViewMode] = useState("calendar"); // calendar | board
  const [calendarRange, setCalendarRange] = useState("month"); // month | week | day
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(new Date());
  const [lightbox, setLightbox] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [editDateVal, setEditDateVal] = useState("");
  const [linkDialog, setLinkDialog] = useState(null);
  const [calendarView, setCalendarView] = useState("month");
  const [calendarDate, setCalendarDate] = useState(new Date());

  // ── Plan Experience State ──
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planMode, setPlanMode] = useState(null); // "single" | "event" | null
  const [planStep, setPlanStep] = useState(0); // 0: choose mode, 1: category/occasion, 2: defaults/inspo, 3: details
  const [planCategory, setPlanCategory] = useState("");
  const [planOccasion, setPlanOccasion] = useState("");
  const [planEventType, setPlanEventType] = useState("");
  const [planEventName, setPlanEventName] = useState("");
  const [planName, setPlanName] = useState("");
  const [planDate, setPlanDate] = useState("");
  const [planTime, setPlanTime] = useState("");
  const [planNoDate, setPlanNoDate] = useState(false);
  const [planNote, setPlanNote] = useState("");
  const [planCreating, setPlanCreating] = useState(false);

  // Iter 9.40 Pass I.2 - Launch Control gating for Plan modal
  const [liveCategories, setLiveCategories] = useState(DEFAULT_LIVE_CATEGORIES);
  const [waitlistCategories, setWaitlistCategories] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/launch-control/live-categories`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d?.live) setLiveCategories(d.live);
        if (d?.waitlist) setWaitlistCategories(d.waitlist);
      })
      .catch(() => { /* keep defaults */ });
  }, []);
  const [userDefaults, setUserDefaults] = useState({});
  const [userInspo, setUserInspo] = useState([]);
  const [selectedInspoIds, setSelectedInspoIds] = useState([]);
  const [uploadingInspo, setUploadingInspo] = useState(false);
  const inspoFileRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showPrefCard, setShowPrefCard] = useState(false);
  const [occasionTypes, setOccasionTypes] = useState([]);
  // ── Event template service deselection ──
  const [deselectedServices, setDeselectedServices] = useState([]);
  // ── Inline Defaults Editing ──
  const [showInlineDefaults, setShowInlineDefaults] = useState(null); // experience_id or "plan"
  const [inlineDefaultsCategory, setInlineDefaultsCategory] = useState(null);
  const [editingDefaults, setEditingDefaults] = useState({});
  const [savingDefaults, setSavingDefaults] = useState(false);
  // ── Provider Selection ──
  const [showProviderModal, setShowProviderModal] = useState(null); // experience_id
  const [providerMode, setProviderMode] = useState("still_deciding"); // "still_deciding" | "manual" | "search"
  const [providerName, setProviderName] = useState("");
  const [providerDetails, setProviderDetails] = useState({ email: "", phone: "", address: "" });
  const [savingProvider, setSavingProvider] = useState(false);
  // ── Preference Card Preview ──
  const [prefCardPreview, setPrefCardPreview] = useState(null); // experience object
  const [prefEditMode, setPrefEditMode] = useState(false); // edit mode toggle
  const [prefEditName, setPrefEditName] = useState("");
  const [prefEditDate, setPrefEditDate] = useState("");
  // ── My Providers (rolodex) ──
  const [myProviders, setMyProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [showProviderForm, setShowProviderForm] = useState(false); // "add" | provider_id | false
  const [providerForm, setProviderForm] = useState({ name: "", category: "", email: "", phone: "", address: "", instagram: "", website: "", notes: "" });
  const [savingProviderForm, setSavingProviderForm] = useState(false);
  // ── All Planned filter ──
  const [allPlannedFilter, setAllPlannedFilter] = useState("all"); // "all" | "occasions" | "individual" | "planning"
  // ── HTML5 Drag-and-Drop state ──
  const [draggingExpId, setDraggingExpId] = useState(null);
  const [dropTargetEventId, setDropTargetEventId] = useState(null);

  useEffect(() => { fetchExperiences(); fetchOccasionTypes(); fetchCurrentUser(); fetchMyProviders(); }, []);

  // Iter 9.40 Pass K - Jessica-plans-this bidirectional sync.
  // Refetch experiences whenever the tab regains focus so host kanban changes
  // appear immediately in the client's /me/experiences view.
  useEffect(() => {
    const onFocus = () => { try { fetchExperiences(); } catch {} };
    const onVis = () => { if (!document.hidden) onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    const id = setInterval(onFocus, 60_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(id);
    };
  }, []);

  // ── HTML5 Drag-and-Drop handlers (drag a service card onto an occasion card) ──
  const handleExpDragStart = (e, exp) => {
    setDraggingExpId(exp.experience_id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", exp.experience_id);
  };
  const handleExpDragEnd = () => {
    setDraggingExpId(null);
    setDropTargetEventId(null);
  };
  const handleOccasionDragOver = (e, eventExpId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTargetEventId !== eventExpId) setDropTargetEventId(eventExpId);
  };
  const handleOccasionDragLeave = () => {
    setDropTargetEventId(null);
  };
  const handleOccasionDrop = async (e, occasionExpId) => {
    e.preventDefault();
    const expId = e.dataTransfer.getData("text/plain") || draggingExpId;
    setDropTargetEventId(null);
    setDraggingExpId(null);
    if (!expId || expId === occasionExpId) return;
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/user/experiences/${expId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ linked_event_id: occasionExpId }),
      });
      if (r.ok) {
        toast.success("Linked to occasion");
        fetchExperiences();
      } else {
        toast.error("Could not link experience");
      }
    } catch {
      toast.error("Could not link experience");
    }
  };

  // Fetch user defaults, inspo, profile, and occasion types when plan modal opens
  useEffect(() => {
    if (showPlanModal) {
      fetchUserDefaults();
      fetchUserInspo();
      fetchCurrentUser();
      fetchOccasionTypes();
    }
  }, [showPlanModal]);

  const fetchUserDefaults = async () => {
    try {
      const r = await axios.get(`${API}/user/defaults`, { withCredentials: true });
      setUserDefaults(r.data?.defaults || {});
    } catch { /* ignore */ }
  };

  const fetchUserInspo = async () => {
    try {
      const r = await axios.get(`${API}/user/inspirations`, { withCredentials: true });
      setUserInspo(Array.isArray(r.data) ? r.data : []);
    } catch { /* ignore */ }
  };

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setCurrentUser(await r.json());
    } catch { /* ignore */ }
  };

  const fetchOccasionTypes = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/me/occasion-types`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setOccasionTypes(data.types || []);
      }
    } catch { /* ignore */ }
  };

  // ── My Providers CRUD ──
  const fetchMyProviders = async () => {
    setLoadingProviders(true);
    try {
      const r = await axios.get(`${API}/user/providers`, { withCredentials: true });
      setMyProviders(r.data?.providers || []);
    } catch { /* ignore */ }
    setLoadingProviders(false);
  };

  const saveProviderForm = async () => {
    if (!providerForm.name.trim()) { toast.error("Provider name is required"); return; }
    setSavingProviderForm(true);
    try {
      if (typeof showProviderForm === "string" && showProviderForm !== "add") {
        // Edit existing
        await axios.patch(`${API}/user/providers/${showProviderForm}`, providerForm, { withCredentials: true });
        toast.success("Provider updated!");
      } else {
        // Add new
        await axios.post(`${API}/user/providers`, providerForm, { withCredentials: true });
        toast.success("Provider saved!");
      }
      fetchMyProviders();
      setShowProviderForm(false);
      setProviderForm({ name: "", category: "", email: "", phone: "", address: "", instagram: "", website: "", notes: "" });
    } catch { toast.error("Failed to save provider"); }
    setSavingProviderForm(false);
  };

  const deleteProvider = async (providerId) => {
    try {
      await axios.delete(`${API}/user/providers/${providerId}`, { withCredentials: true });
      toast.success("Provider removed");
      fetchMyProviders();
    } catch { toast.error("Failed to delete"); }
  };

  const editProvider = (p) => {
    setProviderForm({
      name: p.name || "", category: p.category || "", email: p.email || "",
      phone: p.phone || "", address: p.address || "", instagram: p.instagram || "",
      website: p.website || "", notes: p.notes || ""
    });
    setShowProviderForm(p.provider_id);
  };

  // ── Experience Inspo CRUD (remove/add from preference card) ──
  const removeInspoFromExperience = async (expId, inspoIndex) => {
    try {
      const exp = experiences.find(e => e.experience_id === expId);
      if (!exp) return;
      const newInspo = [...(exp.inspo || [])];
      newInspo.splice(inspoIndex, 1);
      await axios.patch(`${API}/user/experiences/${expId}`, { inspo: newInspo }, { withCredentials: true });
      fetchExperiences();
      // Update the preview if open
      if (prefCardPreview?.experience_id === expId) {
        setPrefCardPreview(prev => ({ ...prev, inspo: newInspo }));
      }
      toast.success("Inspo removed");
    } catch { toast.error("Failed to remove"); }
  };

  const addInspoToExperience = async (expId, inspoItem) => {
    try {
      const exp = experiences.find(e => e.experience_id === expId);
      if (!exp) return;
      const newInspo = [...(exp.inspo || []), inspoItem];
      await axios.patch(`${API}/user/experiences/${expId}`, { inspo: newInspo }, { withCredentials: true });
      fetchExperiences();
      if (prefCardPreview?.experience_id === expId) {
        setPrefCardPreview(prev => ({ ...prev, inspo: newInspo }));
      }
      toast.success("Inspo added!");
    } catch { toast.error("Failed to add"); }
  };

  // Save experience name/date from preference card
  const updateExperienceField = async (expId, field, value) => {
    try {
      await axios.patch(`${API}/user/experiences/${expId}`, { [field]: value }, { withCredentials: true });
      fetchExperiences();
      if (prefCardPreview?.experience_id === expId) {
        setPrefCardPreview(prev => ({ ...prev, [field]: value }));
      }
    } catch { toast.error("Failed to update"); }
  };



  const handleInspoUpload = async (file) => {
    if (!file) return;
    setUploadingInspo(true);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", planCategory || "nails");
      formData.append("tags", JSON.stringify([planCategory].filter(Boolean)));
      const res = await fetch(`${API}/inspirations/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        toast.success("Inspo uploaded!");
        await fetchUserInspo();
      } else {
        toast.error("Upload failed");
      }
    } catch { toast.error("Upload failed"); }
    finally {
      setUploadingInspo(false);
      if (inspoFileRef.current) inspoFileRef.current.value = "";
    }
  };

  const resetPlanState = () => {
    setPlanMode(null); setPlanStep(0); setPlanCategory("");
    setPlanOccasion(""); setPlanEventType(""); setPlanEventName("");
    setPlanName(""); setPlanDate(""); setPlanTime("");
    setPlanNoDate(false); setPlanNote(""); setSelectedInspoIds([]);
    setPlanCreating(false); setDeselectedServices([]);
  };

  const openPlanModal = () => { resetPlanState(); setShowPlanModal(true); };
  const closePlanModal = () => { setShowPlanModal(false); resetPlanState(); };

  // Iter 9.40 Pass F - auto-open the Plan modal when ?plan=service or ?plan=occasion
  // is in the URL (used by /me Dashboard tiles to deep-link straight into planning).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planParam = params.get("plan");
    if (planParam === "service" || planParam === "occasion") {
      resetPlanState();
      setShowPlanModal(true);
      // Clear the param so it doesn't re-fire on every nav
      params.delete("plan");
      const next = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (next ? "?" + next : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle service in event template
  const toggleEventService = (svc) => {
    setDeselectedServices(prev => prev.includes(svc) ? prev.filter(s => s !== svc) : [...prev, svc]);
  };

  // Get active (not deselected) services for the selected event type
  const getActiveEventServices = () => {
    const allSvcs = occasionTypes.find(t => t.id === planEventType)?.suggestedExperiences || [];
    return allSvcs.filter(s => !deselectedServices.includes(s));
  };

  // Inline defaults editing (R7: chip-based, schema-driven)
  // Initializes editingDefaults using schema block keys so the chip modal
  // reads/writes consistently. Falls back to userDefaults for legacy data.
  const openInlineDefaults = (category, expId = "plan") => {
    const schema = getPreferenceSchema(category);
    const currentDefs = {};
    if (schema) {
      schema.blocks.forEach((block) => {
        // Prefer existing schema-key data, else hydrate from any legacy block, else empty
        const fromSchemaKey = userDefaults[block.key];
        const fromLegacy = (CATEGORY_DEFAULTS_MAP[category] || [])
          .map((k) => userDefaults[k])
          .find((v) => v && Object.keys(v).length > 0);
        currentDefs[block.key] = (fromSchemaKey && Object.keys(fromSchemaKey).length > 0)
          ? { ...fromSchemaKey }
          : (fromLegacy ? { ...fromLegacy } : {});
      });
    } else {
      // Legacy fallback: populate using CATEGORY_DEFAULTS_MAP keys
      const catKeys = CATEGORY_DEFAULTS_MAP[category] || [];
      catKeys.forEach((key) => {
        if (userDefaults[key] && Object.keys(userDefaults[key]).length > 0) {
          currentDefs[key] = { ...userDefaults[key] };
        } else {
          const fieldShape = (DEFAULT_BLOCK_FIELDS && DEFAULT_BLOCK_FIELDS[key]) || {};
          currentDefs[key] = { ...fieldShape };
        }
      });
    }
    setInlineDefaultsCategory(category);
    setEditingDefaults(currentDefs);
    setShowInlineDefaults(expId);
  };

  const saveInlineDefaults = async () => {
    setSavingDefaults(true);
    try {
      const token = localStorage.getItem("session_token");
      // Save each category block
      for (const [key, val] of Object.entries(editingDefaults)) {
        await fetch(`${API}/me/defaults/${key}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ value: val })
        });
      }
      // Refresh
      await fetchUserDefaults();
      setShowInlineDefaults(null);
      toast.success("Preferences updated!");
    } catch { toast.error("Failed to save"); }
    finally { setSavingDefaults(false); }
  };

  // Provider selection
  const openProviderModal = (exp) => {
    setShowProviderModal(exp.experience_id);
    setProviderMode(exp.provider_name ? "manual" : "still_deciding");
    setProviderName(exp.provider_name || "");
    setProviderDetails({ email: "", phone: "", address: "" });
  };

  const saveProvider = async () => {
    if (!showProviderModal) return;
    setSavingProvider(true);
    try {
      const payload = {
        provider_name: providerMode === "still_deciding" ? null : providerName.trim() || null,
        provider_status: providerMode,
        provider_details: providerMode === "manual" ? providerDetails : undefined,
      };
      await axios.patch(`${API}/user/experiences/${showProviderModal}`, payload, { withCredentials: true });
      fetchExperiences();
      setShowProviderModal(null);
      toast.success(providerMode === "still_deciding" ? "Provider cleared" : "Provider saved!");
    } catch { toast.error("Failed to save provider"); }
    finally { setSavingProvider(false); }
  };

  // Humanize underscore keys
  const humanize = (s) => s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const relevantDefaultKeys = CATEGORY_DEFAULTS_MAP[planCategory] || [];
  const relevantDefaults = relevantDefaultKeys.reduce((acc, key) => {
    if (userDefaults[key] && Object.keys(userDefaults[key]).length > 0) acc[key] = userDefaults[key];
    return acc;
  }, {});

  // Filter inspo by matching category
  const matchingInspo = userInspo.filter(i => {
    const cat = (i.category || "").toLowerCase();
    return cat === planCategory || cat === planCategory + "s";
  });

  const toggleInspo = (id) => {
    setSelectedInspoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ── Create Single Experience ──
  const createSingleExperience = async () => {
    if (!planName.trim()) { toast.error("Give your service a name"); return; }
    setPlanCreating(true);
    try {
      const payload = {
        name: planName.trim(),
        category: planCategory,
        type: "individual",
        date: planNoDate ? null : planDate || null,
        occasion: planOccasion || null,
        notes: planNote,
        defaults: relevantDefaults,
        inspiration_ids: selectedInspoIds,
        inspo: selectedInspoIds.map(id => {
          const item = userInspo.find(i => i.inspiration_id === id || i.inspo_id === id);
          return item ? { inspiration_id: id, image_url: item.image_url || item.url, category: item.category } : null;
        }).filter(Boolean),
        tags: [planCategory, planOccasion].filter(Boolean),
      };
      await axios.post(`${API}/user/experiences`, payload, { withCredentials: true });
      toast.success("Service planned!");
      closePlanModal();
      fetchExperiences();
    } catch { toast.error("Failed to create service"); }
    finally { setPlanCreating(false); }
  };

  // ── Create Event with Linked Experiences ──
  const createEventExperiences = async () => {
    if (!planEventName.trim()) { toast.error("Name your occasion"); return; }
    setPlanCreating(true);
    try {
      const activeServices = getActiveEventServices();
      if (activeServices.length === 0) { toast.error("Select at least one service"); setPlanCreating(false); return; }
      const payload = {
        name: planEventName.trim(),
        event_type: planEventType,
        date: planNoDate ? null : planDate || null,
        suggested_services: activeServices,
        notes: planNote,
      };
      await axios.post(`${API}/user/experiences/plan-event`, payload, { withCredentials: true });
      toast.success(`${planEventName} planned with ${activeServices.length} linked service${activeServices.length !== 1 ? "s" : ""}!`);
      closePlanModal();
      fetchExperiences();
    } catch { toast.error("Failed to plan occasion"); }
    finally { setPlanCreating(false); }
  };

  const fetchExperiences = async () => {
    try {
      const r = await axios.get(`${API}/user/experiences`, { withCredentials: true });
      setExperiences(Array.isArray(r.data?.experiences) ? r.data.experiences : []);
    } catch { setExperiences([]); }
    finally { setLoading(false); }
  };

  /* ── CRUD Handlers ── */
  const updateDate = async (expId, newDate) => {
    try {
      await axios.patch(`${API}/user/experiences/${expId}`, { date: newDate }, { withCredentials: true });
      setExperiences(prev => prev.map(e => e.experience_id === expId ? { ...e, date: newDate } : e));
      setEditingDate(null);
      toast.success("Date updated");
    } catch { toast.error("Could not update date"); }
  };

  const deleteExperience = async (expId) => {
    try {
      await axios.delete(`${API}/user/experiences/${expId}`, { withCredentials: true });
      setExperiences(prev => prev.filter(e => e.experience_id !== expId));
      toast.success("Experience removed");
    } catch { toast.error("Could not delete"); }
  };

  const unlinkExperience = async (parentId, childId) => {
    try {
      await axios.post(`${API}/user/experiences/${parentId}/unlink`, { child_id: childId }, { withCredentials: true });
      fetchExperiences();
      toast.success("Unlinked");
    } catch { toast.error("Could not unlink"); }
  };

  const linkExperience = async (parentId, childId) => {
    try {
      await axios.post(`${API}/user/experiences/${parentId}/link`, { child_id: childId }, { withCredentials: true });
      fetchExperiences();
      setLinkDialog(null);
      toast.success("Linked!");
    } catch { toast.error("Could not link"); }
  };

  /* ── Helpers ── */
  const getDaysUntil = (d) => {
    if (!d) return null;
    const dt = new Date(d), today = new Date();
    today.setHours(0, 0, 0, 0); dt.setHours(0, 0, 0, 0);
    return Math.ceil((dt - today) / 86400000);
  };
  const fmtDate = (d) => {
    if (!d) return "No date set";
    return new Date(d + (d.length === 10 ? "T12:00:00" : "")).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };
  const openLightbox = (images, idx) => setLightbox({ images, startIndex: idx });

  /* ── Grouping Logic ── */
  const { eventGroups, individualExps, allWithDates, allPlanned } = useMemo(() => {
    // Step 0: Deduplicate by name+category+date
    const deduped = [];
    const seen = new Set();
    for (const e of experiences) {
      const key = `${e.name}|${e.category}|${(e.date || "").slice(0, 10)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(e);
    }

    // Step 1: Identify explicit parent events
    const parentMap = {};
    deduped.forEach(e => {
      if (e.category === "event" || e.type === "personal_event" || e.type === "rsvp" || e.type === "event") {
        parentMap[e.experience_id] = e;
      }
    });

    // Step 2: Map children to parents
    const childParent = {};
    deduped.forEach(e => {
      if (parentMap[e.experience_id]) return;

      // Check if this exp's linked_event or parent_event_id matches a parent
      const linkRef = e.linked_event || e.parent_event_id;
      if (linkRef && typeof linkRef === "string") {
        // Match by name OR by experience_id (backend stores ID in linked_event)
        const matchingParent = Object.values(parentMap).find(
          p => p.name === linkRef || p.experience_id === linkRef
        );
        if (matchingParent) {
          childParent[e.experience_id] = matchingParent.experience_id;
          return;
        }
      }

      // Check if a parent explicitly lists this child in linked_experiences
      for (const [pid, parent] of Object.entries(parentMap)) {
        const refIds = (parent.linked_experiences || []).map(le => typeof le === "string" ? le : le?.experience_id).filter(Boolean);
        if (refIds.includes(e.experience_id)) {
          childParent[e.experience_id] = pid;
          return;
        }
      }
    });

    // Step 3: Build groups
    const groups = Object.values(parentMap).map(parent => ({
      parent,
      children: deduped.filter(e => childParent[e.experience_id] === parent.experience_id),
    })).filter(g => g.children.length > 0 || (!g.parent._isVirtual));

    // Step 4: Individual = not parent, not child
    const indiv = deduped.filter(e => !parentMap[e.experience_id] && !childParent[e.experience_id]);

    // All with dates (for calendar)
    const withDates = deduped.filter(e => e.date);

    return { eventGroups: groups, individualExps: indiv, allWithDates: withDates, allPlanned: deduped };
  }, [experiences]);

  const upcomingEvents = eventGroups.filter(g => { const d = getDaysUntil(g.parent.date); return d === null || d >= 0; });
  const pastEvents = eventGroups.filter(g => { const d = getDaysUntil(g.parent.date); return d !== null && d < 0; });
  const upcomingIndividual = individualExps.filter(e => { const d = getDaysUntil(e.date); return d === null || d >= 0; });
  const pastIndividual = individualExps.filter(e => { const d = getDaysUntil(e.date); return d !== null && d < 0; });

  /* ── Status-based categorization (Booked / Upcoming / Planning / Completed) ── */
  const allFlat = allPlanned;
  // UPCOMING: Only experiences WITH dates that are in the future
  const bookedExps = allFlat.filter(e => {
    const d = getDaysUntil(e.date);
    return e.provider_name && d !== null && d >= 0;
  });
  const upcomingExps = allFlat.filter(e => {
    const d = getDaysUntil(e.date);
    return !e.provider_name && d !== null && d >= 0;
  });
  // No planningExps in Upcoming view - those go to All Planned
  const completedExps = allFlat.filter(e => {
    const d = getDaysUntil(e.date);
    return d !== null && d < 0;
  });
  // ALL PLANNED: Everything including undated
  const planningExps = allFlat.filter(e => !e.date);

  // Category filter (applied to Calendar and All Planned views)
  const matchesCategoryFilter = useCallback((exp) => {
    if (!exp || categoryFilter === "all") return true;
    const c = (exp.category || "").toLowerCase();
    return c === categoryFilter || c === categoryFilter + "s" || c + "s" === categoryFilter;
  }, [categoryFilter]);
  const filteredAllFlat = useMemo(() => allFlat.filter(matchesCategoryFilter), [allFlat, matchesCategoryFilter]);
  const filteredEventGroups = useMemo(() => eventGroups.filter(g => matchesCategoryFilter(g.parent)), [eventGroups, matchesCategoryFilter]);
  const filteredIndividual = useMemo(() => individualExps.filter(matchesCategoryFilter), [individualExps, matchesCategoryFilter]);
  const filteredPlanning = useMemo(() => planningExps.filter(matchesCategoryFilter), [planningExps, matchesCategoryFilter]);
  const visibleCategoryIds = useMemo(() => {
    const ids = new Set();
    allFlat.forEach(e => { if (e.category) ids.add((e.category || "").toLowerCase()); });
    return Array.from(ids);
  }, [allFlat]);

  // Helper: get linked experiences for a given experience
  const getLinkedExperiences = (exp) => {
    if (!exp) return [];
    const parentId = exp.parent_event_id || exp.linked_event;
    // If this IS a parent event, get its children
    if (exp.linked_experiences?.length) {
      return allFlat.filter(e => exp.linked_experiences.includes(e.experience_id) && e.experience_id !== exp.experience_id);
    }
    // If this is a child, get siblings (other children of same parent) + the parent
    if (parentId) {
      const parent = allFlat.find(e => e.experience_id === parentId);
      const siblings = allFlat.filter(e =>
        (e.parent_event_id === parentId || e.linked_event === parentId) && e.experience_id !== exp.experience_id
      );
      const result = [];
      if (parent) result.push(parent);
      result.push(...siblings);
      return result;
    }
    return [];
  };

  // Share experience as preference card
  const shareExperience = async (exp) => {
    try {
      const r = await axios.post(`${API}/user/experiences/${exp.experience_id}/share`, {}, { withCredentials: true });
      const shareId = r.data?.share_id || r.data?.share?.share_id;
      if (shareId) {
        const shareUrl = `${window.location.origin}/share/card/${shareId}`;
        if (navigator.share) {
          navigator.share({ title: exp.name, url: shareUrl });
        } else {
          navigator.clipboard.writeText(shareUrl);
          toast.success("Share link copied!");
        }
      } else {
        toast.error("Could not generate share link");
      }
    } catch { toast.error("Failed to share"); }
  };

  /* ── Sub-Experience Card (child of an event) ── */
  const SubExpCard = ({ exp, parentId }) => {
    const preset = getCategoryPreset(exp.category);
    const Icon = preset?.icon || Calendar;
    return (
      <div className="rounded-xl border p-3 space-y-2 bg-white" style={{ borderColor: (preset?.iconColor || "#d1d5db") + "33" }} data-testid={`sub-exp-${exp.experience_id}`}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: preset?.bgColor || "#f3f4f6" }}>
            <Icon className="w-4 h-4" style={{ color: preset?.iconColor || "#6b7280" }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-900 truncate">{exp.name || exp.category}</p>
            <button
              onClick={(e) => { e.stopPropagation(); openProviderModal(exp); }}
              className="text-[10px] flex items-center gap-1 hover:underline cursor-pointer"
              data-testid={`provider-btn-${exp.experience_id}`}
            >
              {exp.provider_name ? (
                <span className="text-emerald-600 flex items-center gap-1"><UserCheck className="w-3 h-3" />{formatProvider(exp.provider_name)}</span>
              ) : (
                <span className="text-amber-600 italic flex items-center gap-1"><Info className="w-3 h-3" />Still deciding - tap to add</span>
              )}
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPrefCardPreview(exp)} className="text-gray-400 hover:text-purple-600 p-1 flex items-center justify-center" title="View preference card"><Eye className="w-3.5 h-3.5" /></button>
            <button onClick={() => shareExperience(exp)} className="text-gray-400 hover:text-blue-600 p-1 flex items-center justify-center" title="Share"><ArrowUp className="w-3.5 h-3.5" /></button>
            {editingDate === exp.experience_id ? (
              <div className="flex items-center gap-1">
                <Input type="date" value={editDateVal} onChange={e => setEditDateVal(e.target.value)} className="h-6 text-[10px] w-28 px-1" />
                <button onClick={() => updateDate(exp.experience_id, editDateVal)} className="text-emerald-600 hover:text-emerald-700"><Check className="w-3.5 h-3.5" /></button>
                <button onClick={() => setEditingDate(null)} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <>
                <button onClick={() => { setEditingDate(exp.experience_id); setEditDateVal(exp.date?.slice(0, 10) || ""); }}
                  className="text-gray-400 hover:text-gray-600 p-0.5" title="Edit date"><Pencil className="w-3 h-3" /></button>
                {parentId && !exp._isLinkedRef && (
                  <button onClick={() => unlinkExperience(parentId, exp.experience_id)}
                    className="text-gray-400 hover:text-red-500 p-0.5" title="Unlink"><Unlink className="w-3 h-3" /></button>
                )}
                {!parentId && (
                  <button onClick={() => deleteExperience(exp.experience_id)}
                    className="text-gray-400 hover:text-red-500 p-0.5" title="Delete"><Trash2 className="w-3 h-3" /></button>
                )}
              </>
            )}
          </div>
        </div>
        <InspoRow inspo={exp.inspo} onOpen={openLightbox} />
        {exp.date && (
          <p className="text-[10px] text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(exp.date)}</p>
        )}
      </div>
    );
  };

  /* ── Event Group Card (parent + children) ── */
  const EventGroupCard = ({ group }) => {
    const { parent, children } = group;
    const preset = getCategoryPreset(parent.category);
    const Icon = preset?.icon || Calendar;
    const iconColor = preset?.iconColor || "#6b7280";
    const bgColor = preset?.bgColor || "#f3f4f6";
    const days = getDaysUntil(parent.date);
    const isRSVP = parent.from_rsvp || parent.type === "rsvp";

    return (
      <Card
        className={`border bg-white overflow-hidden transition-all ${dropTargetEventId === parent.experience_id ? "border-violet-500 ring-2 ring-violet-200 shadow-lg scale-[1.01]" : "border-gray-100"}`}
        data-testid={`event-group-${parent.experience_id}`}
        onDragOver={(e) => handleOccasionDragOver(e, parent.experience_id)}
        onDragLeave={handleOccasionDragLeave}
        onDrop={(e) => handleOccasionDrop(e, parent.experience_id)}
      >
        <CardContent className="p-0">
          <div className="h-1.5" style={{ backgroundColor: iconColor }} />
          <div className="p-4 space-y-3">
            {/* Header */}
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bgColor }}>
                <Icon className="w-5 h-5" style={{ color: iconColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm leading-tight">{parent.name || "Event"}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  {editingDate === parent.experience_id ? (
                    <div className="flex items-center gap-1">
                      <Input type="date" value={editDateVal} onChange={e => setEditDateVal(e.target.value)} className="h-6 text-xs w-32 px-1" />
                      <button onClick={() => updateDate(parent.experience_id, editDateVal)} className="text-emerald-600"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingDate(null)} className="text-gray-400"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingDate(parent.experience_id); setEditDateVal(parent.date?.slice(0, 10) || ""); }}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 group">
                      <Clock className="w-3 h-3" />{fmtDate(parent.date)}<Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {days !== null && days >= 0 && (
                  <span className={`text-xs font-semibold ${days === 0 ? "text-red-600" : days <= 7 ? "text-amber-600" : "text-gray-400"}`}>
                    {days === 0 ? "Today" : days === 1 ? "Tomorrow" : `${days}d`}
                  </span>
                )}
                <button onClick={() => setPrefCardPreview(parent)} className="text-gray-400 hover:text-purple-600 transition-colors p-0.5 flex items-center justify-center" title="View preference card"><Eye className="w-4 h-4" /></button>
                <button onClick={() => shareExperience(parent)} className="text-gray-400 hover:text-blue-600 transition-colors p-0.5 flex items-center justify-center" title="Share"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => deleteExperience(parent.experience_id)} className="text-gray-300 hover:text-red-500 transition-colors" title="Delete event"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-1.5">
              {isRSVP ? <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">RSVP</Badge> : <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Personal Event</Badge>}
              {parent.provider_name ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] gap-1"><UserCheck className="w-3 h-3" />{formatProvider(parent.provider_name)}</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] gap-1 italic"><Info className="w-3 h-3" />Still deciding</Badge>
              )}
            </div>

            {/* Parent inspo */}
            <InspoRow inspo={parent.inspo} onOpen={openLightbox} />

            {/* Children experiences */}
            {children.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">{children.length} linked experience{children.length !== 1 ? "s" : ""}</p>
                {children.map((c, i) => <SubExpCard key={c.experience_id || i} exp={c} parentId={parent.experience_id} />)}
              </div>
            )}

            {/* Link more button */}
            <button onClick={() => setLinkDialog(parent.experience_id)}
              className="flex items-center gap-1.5 text-[11px] text-purple-600 hover:text-purple-700 font-medium pt-1"
              data-testid="link-more-btn">
              <Plus className="w-3.5 h-3.5" /> Link another experience
            </button>

            {/* Share link */}
            {parent.share_id && (
              <Link to={`/x/${parent.share_id}`} className="flex items-center gap-1 text-[11px] text-violet-600 hover:underline">
                <ExternalLink className="w-3 h-3" /> View Share Card
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  /* ── Individual Experience Card ── */
  const IndividualCard = ({ exp }) => {
    const preset = getCategoryPreset(exp.category);
    const Icon = preset?.icon || Calendar;
    const iconColor = preset?.iconColor || "#6b7280";
    const bgColor = preset?.bgColor || "#f3f4f6";
    const days = getDaysUntil(exp.date);

    return (
      <Card
        className={`border border-gray-100 bg-white overflow-hidden transition-opacity ${draggingExpId === exp.experience_id ? "opacity-50 cursor-grabbing" : "cursor-grab"}`}
        data-testid={`individual-exp-${exp.experience_id}`}
        draggable
        onDragStart={(e) => handleExpDragStart(e, exp)}
        onDragEnd={handleExpDragEnd}
      >
        <CardContent className="p-0">
          <div className="h-1" style={{ backgroundColor: iconColor }} />
          <div className="p-3 space-y-2">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bgColor }}>
                <Icon className="w-4 h-4" style={{ color: iconColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm truncate">{exp.name || "Experience"}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  {editingDate === exp.experience_id ? (
                    <div className="flex items-center gap-1">
                      <Input type="date" value={editDateVal} onChange={e => setEditDateVal(e.target.value)} className="h-6 text-xs w-32 px-1" />
                      <button onClick={() => updateDate(exp.experience_id, editDateVal)} className="text-emerald-600"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingDate(null)} className="text-gray-400"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingDate(exp.experience_id); setEditDateVal(exp.date?.slice(0, 10) || ""); }}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 group">
                      <Clock className="w-3 h-3" />{fmtDate(exp.date)}<Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {days !== null && days >= 0 && (
                  <span className={`text-xs font-semibold ${days === 0 ? "text-red-600" : days <= 7 ? "text-amber-600" : "text-gray-400"}`}>
                    {days === 0 ? "Today" : days === 1 ? "Tomorrow" : `${days}d`}
                  </span>
                )}
                <button onClick={() => setPrefCardPreview(exp)} className="text-gray-400 hover:text-purple-600 transition-colors p-0.5 flex items-center justify-center" title="View preference card" data-testid={`eye-btn-${exp.experience_id}`}><Eye className="w-4 h-4" /></button>
                <button onClick={() => shareExperience(exp)} className="text-gray-400 hover:text-blue-600 transition-colors p-0.5 flex items-center justify-center" title="Share" data-testid={`share-btn-${exp.experience_id}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => deleteExperience(exp.experience_id)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {exp.from_share && <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px] gap-1"><ArrowUp className="w-3 h-3" />Share Card</Badge>}
              <button
                onClick={() => openProviderModal(exp)}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border transition-colors hover:border-gray-400 cursor-pointer"
                data-testid={`provider-badge-${exp.experience_id}`}
              >
                {exp.provider_name ? (
                  <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 px-1 rounded-full"><UserCheck className="w-3 h-3" />{formatProvider(exp.provider_name)}</span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-700 bg-amber-50 px-1 rounded-full italic"><Info className="w-3 h-3" />Add provider</span>
                )}
              </button>
            </div>
            <InspoRow inspo={exp.inspo} onOpen={openLightbox} />
            {exp.share_id && (
              <Link to={`/share/card/${exp.share_id}`} className="flex items-center gap-1 text-[11px] text-violet-600 hover:underline">
                <ExternalLink className="w-3 h-3" /> View Share Card
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  /* ── Calendar helpers ── */
  const getWeekDates = (date) => {
    const s = new Date(date); s.setDate(s.getDate() - s.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; });
  };
  const getMonthDates = (date) => {
    const y = date.getFullYear(), m = date.getMonth();
    const first = new Date(y, m, 1), last = new Date(y, m + 1, 0);
    const days = [];
    for (let i = first.getDay() - 1; i >= 0; i--) days.push({ date: new Date(y, m, -i), current: false });
    for (let d = 1; d <= last.getDate(); d++) days.push({ date: new Date(y, m, d), current: true });
    while (days.length < 42) days.push({ date: new Date(y, m + 1, days.length - last.getDate() - first.getDay() + 1), current: false });
    return days;
  };
  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const getExpsForDate = (date) => allWithDates.filter(e => { const d = new Date(e.date); return isSameDay(d, date); });
  const calNav = (dir) => {
    const d = new Date(calendarDate);
    if (calendarView === "month") d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + 7 * dir);
    setCalendarDate(d);
  };
  const calLabel = calendarView === "month"
    ? calendarDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : (() => { const w = getWeekDates(calendarDate); return `${w[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${w[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`; })();

  /* ── Link Dialog: available experiences to link ── */
  const linkableExps = linkDialog ? experiences.filter(e => {
    if (e.experience_id === linkDialog) return false;
    const parent = eventGroups.find(g => g.parent.experience_id === linkDialog);
    if (parent?.children.some(c => c.experience_id === e.experience_id)) return false;
    if (e.category === "event" || e.type === "personal_event") return false;
    return true;
  }) : [];

  if (loading) {
    return (<div className="min-h-screen flex items-center justify-center bg-white"><div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" /></div>);
  }

  return (
    <div className={embedded ? "pb-4" : "min-h-screen bg-gray-50 pb-32"} data-testid="experiences-page">
      {/* Header - hidden when embedded in /me */}
      {!embedded && (
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <Link to="/me" className="text-gray-500 hover:text-black"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="font-display text-xl font-bold">My Experiences</h1>
          </div>
        </div>
      </header>
      )}
      
      {/* Top tab bar + action buttons (replaces left sidebar for more calendar room) */}
      <div className={embedded ? "px-0 pt-2" : "max-w-6xl mx-auto px-4 sm:px-6"}>
        <div className="flex items-center justify-between flex-wrap gap-2 pt-1 pb-3 border-b border-gray-100 mb-4" data-testid="exp-page-toolbar">
          <div className="flex items-center gap-1" data-testid="exp-top-tabs">
            {[
              { id: "calendar", label: "Calendar", icon: CalendarDays },
              { id: "plan",     label: "All Planned", icon: ListChecks },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveView(tab.id)}
                className={`flex items-center gap-1.5 py-1.5 px-3 rounded-full text-xs font-medium transition-all ${activeView === tab.id ? "bg-black text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"}`}
                data-testid={`tab-${tab.id}`}>
                <tab.icon className="w-3.5 h-3.5 flex-shrink-0" />{tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Button onClick={() => setActiveView("providers")} variant="ghost" size="sm" className="rounded-full h-8 text-xs gap-1.5 text-gray-600" data-testid="my-providers-btn">
              <BookOpen className="w-3.5 h-3.5" />My Providers
              {myProviders.length > 0 && <span className="text-[10px] bg-gray-100 text-gray-600 rounded-full px-1.5">{myProviders.length}</span>}
            </Button>
            <Button onClick={openPlanModal} size="sm" className="rounded-full h-8 text-xs gap-1.5 bg-black hover:bg-gray-800" data-testid="plan-experience-btn">
              <Plus className="w-3.5 h-3.5" />Plan a Service
            </Button>
          </div>
        </div>

        <div className="w-full">

      <div className={embedded ? "space-y-6" : "space-y-6"}>

        {/* ═══ UPCOMING TAB (legacy - hidden by default; reachable only if state forced) ═══ */}
        {activeView === "experiences" && (
          <>
            {/* Board <-> Calendar toggle */}
            <div className="flex items-center justify-end gap-1 px-1" data-testid="exp-view-toggle">
              <button
                onClick={() => setExpViewMode("board")}
                className={`px-3 py-1 rounded-full text-xs font-medium ${expViewMode === "board" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}
                data-testid="exp-view-board"
              >Board</button>
              <button
                onClick={() => setExpViewMode("calendar")}
                className={`px-3 py-1 rounded-full text-xs font-medium ${expViewMode === "calendar" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}
                data-testid="exp-view-calendar"
              >Calendar</button>
            </div>

            {expViewMode === "calendar" ? (
              <GoogleCalendarMonth
                experiences={allFlat.filter(e => e.target_date || e.date)}
                onDateClick={() => {}}
                onExperienceClick={(e) => setPrefCardPreview(e)}
              />
            ) : (
              <>
            {/* Booked - confirmed with provider, upcoming */}
            {bookedExps.length > 0 && (
              <section>
                <h2 className="font-display text-sm font-semibold mb-3 px-1 text-emerald-600 uppercase tracking-wide flex items-center gap-2">
                  <UserCheck className="w-4 h-4" /> Booked ({bookedExps.length})
                </h2>
                <div className="space-y-3">
                  {bookedExps.map(e => <IndividualCard key={e.experience_id} exp={e} />)}
                </div>
              </section>
            )}

            {/* Upcoming - has a date but no provider yet */}
            {upcomingExps.length > 0 && (
              <section>
                <h2 className="font-display text-sm font-semibold mb-3 px-1 text-amber-600 uppercase tracking-wide flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Upcoming ({upcomingExps.length})
                </h2>
                <div className="space-y-3">
                  {upcomingExps.map(e => <IndividualCard key={e.experience_id} exp={e} />)}
                </div>
              </section>
            )}

            {/* My Events (grouped) - event groups with children */}
            {upcomingEvents.length > 0 && (
              <section>
                <h2 className="font-display text-sm font-semibold mb-3 px-1 text-gray-500 uppercase tracking-wide">My Events</h2>
                <div className="space-y-4">
                  {upcomingEvents.map(g => <EventGroupCard key={g.parent.experience_id} group={g} />)}
                </div>
              </section>
            )}

            {/* Completed - past */}
            {completedExps.length > 0 && (
              <section>
                <h2 className="font-display text-sm font-semibold mb-3 px-1 text-gray-400 uppercase tracking-wide flex items-center gap-2">
                  <Check className="w-4 h-4" /> Completed ({completedExps.length})
                </h2>
                <div className="space-y-3 opacity-60">
                  {completedExps.map(e => <IndividualCard key={e.experience_id} exp={e} />)}
                </div>
              </section>
            )}

            {/* Empty */}
            {experiences.length === 0 && (
              <div className="py-8">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4"><Calendar className="w-8 h-8 text-gray-400" /></div>
                <h3 className="font-display text-base font-semibold text-gray-900 mb-1 text-center">No upcoming services</h3>
                <p className="text-gray-500 text-sm max-w-xs mx-auto mb-6 text-center">Plan your next service or prepare for an occasion.</p>
                <div className="space-y-2 max-w-sm mx-auto">
                  <button
                    onClick={() => { openPlanModal(); setTimeout(() => { setPlanMode("single"); setPlanStep(1); }, 100); }}
                    className="w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-3 hover:shadow-sm"
                    style={{ backgroundColor: PLAN_CTAS.service.bgColor, borderColor: `${PLAN_CTAS.service.iconColor}33` }}
                    data-testid="empty-plan-single-btn"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${PLAN_CTAS.service.iconColor}1a` }}>
                      {(() => { const I = PLAN_CTAS.service.icon; return <I className="w-5 h-5" style={{ color: PLAN_CTAS.service.iconColor }} />; })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{PLAN_CTAS.service.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{PLAN_CTAS.service.subtitle}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                  <button
                    onClick={() => { openPlanModal(); setTimeout(() => { setPlanMode("event"); setPlanStep(1); }, 100); }}
                    className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-black bg-gray-50 transition-all text-left flex items-center gap-3"
                    data-testid="empty-plan-event-btn"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                      {(() => { const I = PLAN_CTAS.occasion.icon; return <I className="w-5 h-5 text-gray-700" />; })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{PLAN_CTAS.occasion.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{PLAN_CTAS.occasion.subtitle}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                </div>
              </div>
            )}
              </>
            )}
          </>
        )}

        {/* ═══ CALENDAR TAB ═══ */}
        {activeView === "calendar" && (
          <div className="space-y-3" data-testid="calendar-view">
            {/* Top filter row: category pills + Month/Week/Day toggle */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex gap-1.5 flex-wrap items-center" data-testid="exp-category-filter">
                <button
                  onClick={() => setCategoryFilter("all")}
                  className={`text-[11px] px-2.5 py-1 rounded-full transition-all ${categoryFilter === "all" ? "bg-black text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  data-testid="cat-filter-all"
                >All ({allFlat.length})</button>
                {visibleCategoryIds.map(catId => {
                  const p = getCategoryPreset(catId);
                  const count = allFlat.filter(e => (e.category || "").toLowerCase() === catId).length;
                  const active = categoryFilter === catId;
                  return (
                    <button
                      key={catId}
                      onClick={() => setCategoryFilter(active ? "all" : catId)}
                      className="text-[11px] px-2.5 py-1 rounded-full transition-all flex items-center gap-1 capitalize"
                      style={active ? { backgroundColor: p?.iconColor || "#111", color: "#fff" } : { backgroundColor: p?.bgColor || "#f3f4f6", color: p?.iconColor || "#6b7280" }}
                      data-testid={`cat-filter-${catId}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: active ? "#fff" : p?.iconColor || "#6b7280" }} />
                      {p?.name || catId} ({count})
                    </button>
                  );
                })}
              </div>
              <div className="flex border border-gray-200 rounded-full overflow-hidden text-xs" data-testid="cal-range-toggle">
                {["month","week","day"].map(r => (
                  <button
                    key={r}
                    onClick={() => setCalendarRange(r)}
                    className={`px-3 py-1 capitalize transition-colors ${calendarRange === r ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
                    data-testid={`cal-range-${r}`}
                  >{r}</button>
                ))}
              </div>
            </div>

            {/* Month view */}
            {calendarRange === "month" && (
              <GoogleCalendarMonth
                selectedDate={calendarSelectedDate}
                onDateSelect={setCalendarSelectedDate}
                experiences={filteredAllFlat.filter(e => e.target_date || e.date)}
                onViewExperience={(e) => setPrefCardPreview(e)}
              />
            )}

            {/* Week view */}
            {calendarRange === "week" && (
              <CalendarWeekView
                selectedDate={calendarSelectedDate}
                onDateSelect={setCalendarSelectedDate}
                experiences={filteredAllFlat.filter(e => e.target_date || e.date)}
                onViewExperience={(e) => setPrefCardPreview(e)}
              />
            )}

            {/* Day view: simple list of that-day experiences */}
            {calendarRange === "day" && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="calendar-day-view">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { const d = new Date(calendarSelectedDate); d.setDate(d.getDate()-1); setCalendarSelectedDate(d); }}
                      className="p-1.5 rounded hover:bg-gray-200" data-testid="day-prev"
                    ><ChevronLeft className="w-4 h-4" /></button>
                    <button
                      onClick={() => setCalendarSelectedDate(new Date())}
                      className="text-xs px-3 py-1 rounded-full border border-gray-200 hover:bg-gray-100" data-testid="day-today"
                    >Today</button>
                    <button
                      onClick={() => { const d = new Date(calendarSelectedDate); d.setDate(d.getDate()+1); setCalendarSelectedDate(d); }}
                      className="p-1.5 rounded hover:bg-gray-200" data-testid="day-next"
                    ><ChevronRight className="w-4 h-4" /></button>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {calendarSelectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </h3>
                </div>
                <div className="p-4 space-y-2">
                  {(() => {
                    const dayExps = filteredAllFlat.filter(e => {
                      const d = new Date(e.target_date || e.date);
                      return d.toDateString() === calendarSelectedDate.toDateString();
                    });
                    if (dayExps.length === 0) {
                      return <p className="text-xs text-gray-400 text-center py-8">No experiences on this day.</p>;
                    }
                    return dayExps.map(exp => {
                      const p = getCategoryPreset(exp.category);
                      const Icon = p?.icon || Calendar;
                      return (
                        <button
                          key={exp.experience_id}
                          onClick={() => setPrefCardPreview(exp)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white hover:border-gray-300 transition-all text-left"
                          data-testid={`day-exp-${exp.experience_id}`}
                        >
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: p?.bgColor || "#f3f4f6" }}>
                            <Icon className="w-4 h-4" style={{ color: p?.iconColor || "#6b7280" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{exp.name}</p>
                            <p className="text-[11px] text-gray-500 capitalize">{p?.name || exp.category}{exp.provider_name ? ` · ${exp.provider_name}` : ""}</p>
                          </div>
                          <Eye className="w-4 h-4 text-gray-400" />
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ LEGACY CALENDAR (unreachable - kept until full extract) ═══ */}
        {false && activeView === "calendar_legacy" && (
          <div className="space-y-4" data-testid="calendar-view-legacy">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">{calLabel}</h2>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setCalendarDate(new Date())} className="text-xs h-7">Today</Button>
                <div className="flex border rounded-lg overflow-hidden">
                  <button onClick={() => calNav(-1)} className="p-1.5 hover:bg-gray-100"><ChevronLeft className="w-3.5 h-3.5" /></button>
                  <button onClick={() => calNav(1)} className="p-1.5 hover:bg-gray-100"><ChevronRight className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex border rounded-lg overflow-hidden text-xs">
                  {["month", "week"].map(v => (
                    <button key={v} onClick={() => setCalendarView(v)}
                      className={`px-2.5 py-1.5 capitalize ${calendarView === v ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"}`}
                      data-testid={`cal-${v}`}>{v}</button>
                  ))}
                </div>
              </div>
            </div>

            {calendarView === "month" && (
              <div className="border rounded-2xl overflow-hidden bg-white">
                <div className="grid grid-cols-7 bg-gray-50 border-b">
                  {["S","M","T","W","T","F","S"].map((d,i) => <div key={i} className="text-center text-[10px] font-medium text-gray-400 py-2">{d}</div>)}
                </div>
                <div className="grid grid-cols-7">
                  {getMonthDates(calendarDate).map((day, i) => {
                    const dayExps = getExpsForDate(day.date);
                    const isToday = isSameDay(day.date, new Date());
                    return (
                      <div key={i} className={`min-h-[72px] border-b border-r p-1 text-left ${!day.current ? "bg-gray-50/50" : "bg-white"} ${isToday ? "ring-1 ring-inset ring-black" : ""}`}>
                        <span className={`text-[10px] font-medium ${isToday ? "bg-black text-white w-5 h-5 rounded-full inline-flex items-center justify-center" : day.current ? "text-gray-700" : "text-gray-300"}`}>{day.date.getDate()}</span>
                        {dayExps.slice(0, 2).map((ev, j) => {
                          const p = getCategoryPreset(ev.category);
                          const firstInspo = ev.inspo?.[0];
                          const inspoUrl = firstInspo ? (typeof firstInspo === "string" ? firstInspo : firstInspo?.image_url) : null;
                          return (
                            <div key={j} className="mt-0.5 px-1 py-0.5 rounded text-[9px] truncate flex items-center gap-1" style={{ backgroundColor: p?.bgColor || "#f3f4f6", color: p?.iconColor || "#6b7280" }}>
                              {inspoUrl && <img src={inspoUrl} alt="" className="w-4 h-4 rounded object-cover flex-shrink-0" />}
                              <span className="truncate">{ev.name || "Exp"}</span>
                            </div>
                          );
                        })}
                        {dayExps.length > 2 && <p className="text-[8px] text-gray-500 pl-1">+{dayExps.length - 2}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {calendarView === "week" && (
              <div className="border rounded-2xl overflow-hidden bg-white">
                <div className="grid grid-cols-7 bg-gray-50 border-b">
                  {getWeekDates(calendarDate).map((d, i) => {
                    const isToday = isSameDay(d, new Date());
                    return (
                      <div key={i} className={`text-center py-2.5 ${isToday ? "bg-black/5" : ""}`}>
                        <p className="text-[10px] text-gray-400">{d.toLocaleDateString("en-US", { weekday: "short" })}</p>
                        <p className={`text-sm font-semibold ${isToday ? "text-black" : "text-gray-700"}`}>{d.getDate()}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-7 min-h-[300px]">
                  {getWeekDates(calendarDate).map((d, i) => {
                    const dayExps = getExpsForDate(d);
                    return (
                      <div key={i} className="border-r p-1.5 space-y-1.5">
                        {dayExps.map((ev, j) => {
                          const p = getCategoryPreset(ev.category);
                          const firstInspo = ev.inspo?.[0];
                          const inspoUrl = firstInspo ? (typeof firstInspo === "string" ? firstInspo : firstInspo?.image_url) : null;
                          return (
                            <div key={j} className="p-1.5 rounded-lg border space-y-1" style={{ backgroundColor: p?.bgColor || "#f3f4f6", borderColor: (p?.iconColor || "#d1d5db") + "33" }}>
                              <p className="text-[10px] font-medium text-gray-900 truncate">{ev.name || "Exp"}</p>
                              <p className="text-[8px]" style={{ color: p?.iconColor || "#6b7280" }}>{formatProvider(ev.provider_name) || "Still deciding"}</p>
                              {inspoUrl && (
                                <button onClick={() => openLightbox(ev.inspo, 0)} className="cursor-zoom-in">
                                  <img src={inspoUrl} alt="" className="w-full h-10 rounded object-cover" loading="lazy" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ ALL PLANNED TAB ═══ */}
        {activeView === "plan" && (
          <div data-testid="plan-view">
            <div className="flex gap-6 max-w-5xl mx-auto">
              {/* Left filter sidebar */}
              <aside className="w-44 flex-shrink-0 space-y-5 sticky top-24 self-start" data-testid="plan-filters-sidebar">
                {/* Category */}
                <div className="space-y-1.5" data-testid="exp-category-filter-plan">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-1">Category</p>
                  <button
                    onClick={() => setCategoryFilter("all")}
                    className={`w-full text-left text-[11px] px-2.5 py-1.5 rounded-lg transition-all flex items-center justify-between ${categoryFilter === "all" ? "bg-black text-white" : "text-gray-600 hover:bg-gray-100"}`}
                    data-testid="cat-filter-plan-all"
                  >
                    <span>All</span><span className="text-[10px] opacity-70">{allFlat.length}</span>
                  </button>
                  {visibleCategoryIds.map(catId => {
                    const p = getCategoryPreset(catId);
                    const count = allFlat.filter(e => (e.category || "").toLowerCase() === catId).length;
                    const active = categoryFilter === catId;
                    return (
                      <button
                        key={catId}
                        onClick={() => setCategoryFilter(active ? "all" : catId)}
                        className="w-full text-left text-[11px] px-2.5 py-1.5 rounded-lg transition-all flex items-center justify-between capitalize"
                        style={active ? { backgroundColor: p?.iconColor || "#111", color: "#fff" } : { color: "#4b5563" }}
                        data-testid={`cat-filter-plan-${catId}`}
                      >
                        <span className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: active ? "#fff" : p?.iconColor || "#6b7280" }} />
                          {p?.name || catId}
                        </span>
                        <span className="text-[10px] opacity-70">{count}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Group */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-1">Type</p>
                  {[
                    { id: "all", label: "All", count: filteredAllFlat.length },
                    { id: "occasions", label: "Occasions", count: filteredEventGroups.length },
                    { id: "individual", label: "Individual Services", count: filteredIndividual.length },
                    { id: "planning", label: "Planning", count: filteredPlanning.length },
                  ].map(f => (
                    <button key={f.id} onClick={() => setAllPlannedFilter(f.id)}
                      className={`w-full text-left text-[11px] px-2.5 py-1.5 rounded-lg transition-all flex items-center justify-between ${allPlannedFilter === f.id ? "bg-black text-white" : "text-gray-600 hover:bg-gray-100"}`}
                      data-testid={`plan-filter-${f.id}`}>
                      <span>{f.label}</span><span className="text-[10px] opacity-70">{f.count}</span>
                    </button>
                  ))}
                </div>
              </aside>

              {/* Right content (cards) */}
              <div className="flex-1 min-w-0 max-w-2xl space-y-6">

              {/* Events / Occasions Section */}
              {(allPlannedFilter === "all" || allPlannedFilter === "occasions") && filteredEventGroups.length > 0 && (
                <section>
                  <h2 className="font-display text-sm font-semibold mb-3 px-1 text-gray-500 uppercase tracking-wide flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" /> Occasions ({filteredEventGroups.length})
                  </h2>
                  <div className="space-y-4">
                    {filteredEventGroups.map(g => <EventGroupCard key={g.parent.experience_id} group={g} />)}
                  </div>
                </section>
              )}

              {/* Individual Services Section */}
              {(allPlannedFilter === "all" || allPlannedFilter === "individual") && filteredIndividual.length > 0 && (
                <section>
                  <h2 className="font-display text-sm font-semibold mb-3 px-1 text-gray-500 uppercase tracking-wide flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4" /> Individual Services ({filteredIndividual.length})
                    {filteredEventGroups.length > 0 && (
                      <span className="ml-auto text-[10px] normal-case text-violet-600 font-medium flex items-center gap-1" data-testid="drag-drop-hint">
                        <GripVertical className="w-3 h-3" /> Drag onto an Occasion to link
                      </span>
                    )}
                  </h2>
                  <div className="space-y-3">
                    {filteredIndividual.map(e => <IndividualCard key={e.experience_id} exp={e} />)}
                  </div>
                </section>
              )}

              {/* Planning - no date set */}
              {(allPlannedFilter === "all" || allPlannedFilter === "planning") && filteredPlanning.length > 0 && (
                <section>
                  <h2 className="font-display text-sm font-semibold mb-3 px-1 text-violet-600 uppercase tracking-wide flex items-center gap-2">
                    <Info className="w-4 h-4" /> Planning ({filteredPlanning.length})
                  </h2>
                  <div className="space-y-3">
                    {filteredPlanning.map(e => <IndividualCard key={e.experience_id} exp={e} />)}
                  </div>
                </section>
              )}

              {filteredAllFlat.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">{categoryFilter === "all" ? "No planned experiences yet" : `No ${categoryFilter} experiences planned`}</p>
                </div>
              )}

              </div>
            </div>
          </div>
        )}

        {/* ═══ MY PROVIDERS VIEW ═══ */}
        {activeView === "providers" && (
          <div className="space-y-4" data-testid="providers-view">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> My Providers ({myProviders.length})
              </h2>
              <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs gap-1.5 border-dashed" onClick={() => { setProviderForm({ name: "", category: "", email: "", phone: "", address: "", instagram: "", website: "", notes: "" }); setShowProviderForm("add"); }} data-testid="add-provider-btn">
                <Plus className="w-3 h-3" /> Add Provider
              </Button>
            </div>

            {/* Provider Form Dialog */}
            <Dialog open={!!showProviderForm} onOpenChange={v => !v && setShowProviderForm(false)}>
              <DialogContent className="max-w-sm" data-testid="provider-form-dialog">
                <DialogHeader>
                  <DialogTitle>{showProviderForm === "add" ? "Add Provider" : "Edit Provider"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Name *</label>
                    <Input value={providerForm.name} onChange={e => setProviderForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Zuri Hair Lab" className="h-8 text-sm" data-testid="pf-name" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
                    <div className="flex flex-wrap gap-1.5">
                      {CATEGORY_ORDER.slice(0, 11).map(cat => {
                        const p = getCategoryPreset(cat);
                        return (
                          <button key={cat} onClick={() => setProviderForm(pf => ({...pf, category: pf.category === cat ? "" : cat}))}
                            className={`text-[10px] px-2 py-1 rounded-full border transition-all capitalize ${providerForm.category === cat ? "border-black bg-black text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}>
                            {p?.name || cat}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Email</label>
                      <Input value={providerForm.email} onChange={e => setProviderForm(p => ({...p, email: e.target.value}))} placeholder="email@..." className="h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label>
                      <Input value={providerForm.phone} onChange={e => setProviderForm(p => ({...p, phone: e.target.value}))} placeholder="(555)..." className="h-8 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Address</label>
                    <Input value={providerForm.address} onChange={e => setProviderForm(p => ({...p, address: e.target.value}))} placeholder="123 Main St..." className="h-8 text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Instagram</label>
                      <Input value={providerForm.instagram} onChange={e => setProviderForm(p => ({...p, instagram: e.target.value}))} placeholder="@handle" className="h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Website</label>
                      <Input value={providerForm.website} onChange={e => setProviderForm(p => ({...p, website: e.target.value}))} placeholder="https://..." className="h-8 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
                    <Textarea value={providerForm.notes} onChange={e => setProviderForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Any notes..." className="text-sm" />
                  </div>
                  <Button onClick={saveProviderForm} disabled={savingProviderForm || !providerForm.name.trim()} className="w-full bg-black hover:bg-gray-800" data-testid="save-pf-btn">
                    {savingProviderForm ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Save Provider"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Provider Cards */}
            {loadingProviders ? (
              <div className="text-center py-12"><div className="w-6 h-6 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto" /></div>
            ) : myProviders.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-gray-200 rounded-2xl">
                <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No providers saved yet</p>
                <p className="text-xs text-gray-400 mt-1">Save your providers' names, contacts, and notes here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {myProviders.map(p => {
                  const catPreset = p.category ? getCategoryPreset(p.category) : null;
                  const CatIcon = catPreset?.icon || BookOpen;
                  return (
                    <div key={p.provider_id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-white hover:border-gray-200 transition-all" data-testid={`provider-card-${p.provider_id}`}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: catPreset?.bgColor || "#f3f4f6" }}>
                        <CatIcon className="w-5 h-5" style={{ color: catPreset?.iconColor || "#6b7280" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                          {p.category && <span className="text-[9px] px-1.5 py-0.5 rounded-full capitalize" style={{ backgroundColor: (catPreset?.bgColor || "#f3f4f6"), color: catPreset?.iconColor || "#6b7280" }}>{catPreset?.name || p.category}</span>}
                          {p.phygital_profile && (
                            <a href={`/p/${p.phygital_user_id}`} target="_blank" rel="noreferrer" className="text-[9px] text-purple-600 hover:underline flex items-center gap-0.5">
                              <ExternalLink className="w-2.5 h-2.5" />On Phygital
                            </a>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                          {p.email && <span className="text-[10px] text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</span>}
                          {p.phone && <span className="text-[10px] text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
                          {p.address && <span className="text-[10px] text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{p.address}</span>}
                          {p.instagram && <span className="text-[10px] text-gray-500 flex items-center gap-1"><Instagram className="w-3 h-3" />@{p.instagram.replace("@","")}</span>}
                          {p.website && <span className="text-[10px] text-gray-500 flex items-center gap-1"><Globe className="w-3 h-3" />{p.website}</span>}
                        </div>
                        {p.notes && <p className="text-[10px] text-gray-400 mt-1 italic">{p.notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => editProvider(p)} className="text-gray-300 hover:text-gray-600 p-1"><Pencil className="w-3 h-3" /></button>
                        <button onClick={() => deleteProvider(p.provider_id)} className="text-gray-300 hover:text-red-500 p-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
        </div>
      </div>

      {/* Link Dialog */}
      <Dialog open={!!linkDialog} onOpenChange={v => !v && setLinkDialog(null)}>
        <DialogContent className="max-w-md" data-testid="link-dialog">
          <DialogHeader><DialogTitle>Link an Experience</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {linkableExps.length === 0 && <p className="text-sm text-gray-500 py-4 text-center">No available experiences to link</p>}
            {linkableExps.map(e => {
              const p = getCategoryPreset(e.category);
              const Icon = p?.icon || Calendar;
              return (
                <button key={e.experience_id} onClick={() => linkExperience(linkDialog, e.experience_id)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-purple-300 hover:bg-purple-50/30 transition-all text-left"
                  data-testid={`link-option-${e.experience_id}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: p?.bgColor || "#f3f4f6" }}>
                    <Icon className="w-4 h-4" style={{ color: p?.iconColor || "#6b7280" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{e.name}</p>
                    <p className="text-xs text-gray-500">{e.category} {e.date ? `- ${fmtDate(e.date)}` : ""}</p>
                  </div>
                  <Link2 className="w-4 h-4 text-purple-400" />
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {lightbox && <InspoLightbox images={lightbox.images} startIndex={lightbox.startIndex} onClose={() => setLightbox(null)} />}

      {/* ═══ PLAN EXPERIENCE (NEW Iter 9.40 - unified modal replaces legacy 3-step wizard) ═══ */}
      {/* Entry picker: choose Service vs Occasion */}
      <Dialog open={showPlanModal && !planMode} onOpenChange={(open) => { if (!open) closePlanModal(); }}>
        <DialogContent className="max-w-sm p-4 sm:p-6" data-testid="plan-experience-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5" /> Plan
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">What are you planning?</p>
            <button
              onClick={() => setPlanMode("single")}
              className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-black hover:bg-gray-50 transition-all text-left flex items-center gap-4"
              data-testid="plan-single-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Plan a Service</p>
                <p className="text-xs text-gray-500 mt-0.5">One service - pick category, date, provider, preferences</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
            </button>
            <button
              onClick={() => setPlanMode("event")}
              className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-black hover:bg-gray-50 transition-all text-left flex items-center gap-4"
              data-testid="plan-event-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                <PartyPopper className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Plan an Occasion</p>
                <p className="text-xs text-gray-500 mt-0.5">Wedding, birthday, vacation - multiple linked services</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unified Plan-a-Service modal - Launch Control gated, inline date picker, preference sync */}
      <PlanExperienceModal
        isOpen={showPlanModal && planMode === "single"}
        onClose={closePlanModal}
        liveCategoryIds={liveCategories}
        waitlistCategoryIds={waitlistCategories}
        onSelect={async (payload) => {
          try {
            await axios.post(`${API}/user/experiences`, {
              name: payload.name,
              category: String(payload.category || "").replace(/^cat_/, ""),
              type: "individual",
              date: payload.date,
              target_date: payload.target_date,
              provider_status: payload.provider_status,
              provider_name: payload.provider_name,
              provider_phone: payload.provider_phone,
              provider_email: payload.provider_email,
              provider_user_id: payload.provider_user_id,
              provider_user_ids: payload.provider_user_ids,
              linked_product_ids: payload.linked_product_ids,
              linked_event_id: payload.linked_event_id,
              inspo: payload.inspo,
              cover_image: payload.cover_image,
              preferences: payload.preferences,
              sizes: payload.sizes,
              brand_id: payload.brand_id,
              style_link_category: payload.style_link_category,
              tags: [payload.category].filter(Boolean),
            }, { withCredentials: true });
            toast.success("Service planned!");
            closePlanModal();
            fetchExperiences();
          } catch { toast.error("Failed to create service"); }
        }}
      />

      {/* Unified Plan-an-Occasion modal */}
      <PlanEventModal
        isOpen={showPlanModal && planMode === "event"}
        onClose={closePlanModal}
        onSelect={async (payload) => {
          try {
            await axios.post(`${API}/user/experiences/plan-event`, {
              name: payload.name,
              event_type: payload.event_type,
              date: payload.date,
              suggested_services: payload.services,
              service_providers: payload.service_providers,
              service_dates: payload.service_dates,
              service_provider_details: payload.service_provider_details,
            }, { withCredentials: true });
            toast.success(`${payload.name} planned!`);
            closePlanModal();
            fetchExperiences();
          } catch { toast.error("Failed to plan occasion"); }
        }}
      />

      <MobileNav />

      {/* ═══ INLINE DEFAULTS EDITING DIALOG (R7: chip-based, schema-driven) ═══ */}
      <Dialog open={!!showInlineDefaults} onOpenChange={(v) => !v && setShowInlineDefaults(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="inline-defaults-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {(() => {
                const preset = inlineDefaultsCategory && getCategoryPreset(inlineDefaultsCategory);
                const PresetIcon = preset?.icon || Palette;
                return (
                  <>
                    <PresetIcon className="w-5 h-5" style={{ color: preset?.iconColor }} />
                    {preset?.name || (inlineDefaultsCategory && humanize(inlineDefaultsCategory))} Preferences
                  </>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2" data-testid="inline-defaults-content">
            {(() => {
              const schema = getPreferenceSchema(inlineDefaultsCategory);
              if (!schema) {
                return (
                  <div className="text-center py-6">
                    <Palette className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No preference schema defined for {inlineDefaultsCategory}.</p>
                  </div>
                );
              }
              const accent = getCategoryPreset(inlineDefaultsCategory)?.iconColor || "#9c57f5";
              return schema.blocks.map((block) => {
                const blockVal = editingDefaults[block.key] || {};
                return (
                  <div key={block.key} className="space-y-3">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-testid={`pref-block-${block.key}`}>
                      {block.label}
                    </h4>
                    {block.fields.map((field) => {
                      const currentVal = blockVal[field.id];
                      const isSelected = (opt) => {
                        if (field.type === "multi") return Array.isArray(currentVal) && currentVal.includes(opt);
                        return currentVal === opt;
                      };
                      const toggleChip = (opt) => {
                        setEditingDefaults((prev) => {
                          const prevBlock = prev[block.key] || {};
                          if (field.type === "multi") {
                            const existing = Array.isArray(prevBlock[field.id]) ? prevBlock[field.id] : [];
                            const next = existing.includes(opt)
                              ? existing.filter((v) => v !== opt)
                              : [...existing, opt];
                            return { ...prev, [block.key]: { ...prevBlock, [field.id]: next } };
                          }
                          // single: toggle off if already selected
                          const next = prevBlock[field.id] === opt ? "" : opt;
                          return { ...prev, [block.key]: { ...prevBlock, [field.id]: next } };
                        });
                      };
                      return (
                        <div key={field.id} className="space-y-1.5" data-testid={`pref-field-${field.id}`}>
                          <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                            {field.label}
                            {field.type === "multi" && (
                              <span className="text-[9px] uppercase tracking-wider text-gray-400">multi</span>
                            )}
                          </label>
                          <div className="flex flex-wrap gap-1.5">
                            {field.options.map((opt) => {
                              const sel = isSelected(opt);
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => toggleChip(opt)}
                                  className="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
                                  style={{
                                    backgroundColor: sel ? accent : "#fff",
                                    color: sel ? "#fff" : "#374151",
                                    borderColor: sel ? accent : "#e5e7eb",
                                  }}
                                  data-testid={`pref-chip-${field.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
          {/* Sticky save bar: always shown when there's anything to save */}
          <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-white border-t flex gap-3">
            <Button variant="outline" onClick={() => setShowInlineDefaults(null)} className="flex-1" data-testid="cancel-inline-defaults-btn">Cancel</Button>
            <Button onClick={saveInlineDefaults} disabled={savingDefaults} className="flex-1 bg-black hover:bg-gray-800" data-testid="save-inline-defaults-btn">
              {savingDefaults ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Save preferences"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ PROVIDER SELECTION DIALOG ═══ */}
      <Dialog open={!!showProviderModal} onOpenChange={(v) => !v && setShowProviderModal(null)}>
        <DialogContent className="max-w-sm" data-testid="provider-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Who's doing this service?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* 3 tiles: Still deciding · Self-service · Add Provider.
                Add Provider opens an inline list of My Providers with a
                "+ Add new" CTA at the top. Self-service hides ALL provider
                fields because the user IS the provider. */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setProviderMode("still_deciding")}
                className={`p-3 rounded-xl border-2 text-left transition-all ${providerMode === "still_deciding" ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="provider-mode-deciding"
              >
                <Info className={`w-4 h-4 mb-1 ${providerMode === "still_deciding" ? "text-black" : "text-gray-400"}`} />
                <p className="text-[11px] font-medium leading-tight">Still deciding</p>
              </button>
              <button
                onClick={() => setProviderMode("self_service")}
                className={`p-3 rounded-xl border-2 text-left transition-all ${providerMode === "self_service" ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="provider-mode-self-service"
              >
                <UserCheck className={`w-4 h-4 mb-1 ${providerMode === "self_service" ? "text-black" : "text-gray-400"}`} />
                <p className="text-[11px] font-medium leading-tight">Self-service</p>
              </button>
              <button
                onClick={() => { setProviderMode("add_provider"); setAddProviderShowManual(false); }}
                className={`p-3 rounded-xl border-2 text-left transition-all ${providerMode === "add_provider" ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="provider-mode-add-provider"
              >
                <BookOpen className={`w-4 h-4 mb-1 ${providerMode === "add_provider" ? "text-black" : "text-gray-400"}`} />
                <p className="text-[11px] font-medium leading-tight">Add Provider</p>
              </button>
            </div>

            {/* Self-service explainer (no fields - user is the provider) */}
            {providerMode === "self_service" && (
              <div className="p-3 bg-gray-50 rounded-xl text-xs text-gray-600 animate-in slide-in-from-top-2 duration-200" data-testid="self-service-explainer">
                You're handling this one yourself. No provider info needed; we'll save this as a self-service experience.
              </div>
            )}

            {/* Add Provider: list of My Providers with "+ Add new" at top */}
            {providerMode === "add_provider" && !addProviderShowManual && (
              <div className="space-y-2 max-h-72 overflow-y-auto animate-in slide-in-from-top-2 duration-200">
                <button
                  onClick={() => { setAddProviderShowManual(true); setProviderName(""); setProviderDetails({ email: "", phone: "", instagram: "" }); }}
                  className="w-full text-left p-3 rounded-xl border-2 border-dashed border-gray-300 hover:border-black flex items-center gap-3"
                  data-testid="add-new-provider-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center shrink-0">
                    <Plus className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Add new</p>
                    <p className="text-[11px] text-gray-500">Enter a provider not in your list</p>
                  </div>
                </button>
                {myProviders.length === 0 ? (
                  <p className="text-xs text-gray-500 italic px-2">No providers saved yet. Tap "Add new" to enter one.</p>
                ) : (
                  myProviders.map((p) => (
                    <button
                      key={p.provider_id}
                      onClick={() => {
                        setProviderName(p.name || "");
                        setProviderDetails({ email: p.email || "", phone: p.phone || "", instagram: p.instagram || "" });
                      }}
                      className={`w-full text-left p-3 rounded-xl border ${providerName === p.name ? "border-violet-500 bg-violet-50" : "border-gray-200 hover:border-gray-300"} flex items-center gap-3`}
                      data-testid={`pick-provider-${p.provider_id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-700 shrink-0">
                        {(p.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        {p.category && <p className="text-[11px] text-gray-500 capitalize">{p.category}</p>}
                      </div>
                      {providerName === p.name && <Check className="w-4 h-4 text-violet-600" />}
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Add Provider > Add new: manual entry fields */}
            {providerMode === "add_provider" && addProviderShowManual && (
              <div className="space-y-2 animate-in slide-in-from-top-2 duration-200" data-testid="add-provider-manual">
                <button
                  onClick={() => setAddProviderShowManual(false)}
                  className="text-[11px] text-gray-500 hover:text-black flex items-center gap-1"
                  data-testid="add-provider-back"
                >
                  <ChevronLeft className="w-3 h-3" /> Back to my providers
                </button>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Provider name *</label>
                  <Input value={providerName} onChange={(e) => setProviderName(e.target.value)} placeholder="e.g. Aria James Beauty" className="h-8 text-sm" data-testid="provider-name-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Email (optional)</label>
                  <Input value={providerDetails.email} onChange={(e) => setProviderDetails(p => ({...p, email: e.target.value}))} placeholder="provider@email.com" className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Phone (optional)</label>
                  <Input value={providerDetails.phone} onChange={(e) => setProviderDetails(p => ({...p, phone: e.target.value}))} placeholder="(555) 123-4567" className="h-8 text-sm" />
                </div>
              </div>
            )}

            <Button
              onClick={saveProvider}
              disabled={savingProvider || (providerMode === "add_provider" && addProviderShowManual && !providerName.trim()) || (providerMode === "add_provider" && !addProviderShowManual && !providerName.trim())}
              className="w-full bg-black hover:bg-gray-800"
              data-testid="save-provider-btn"
            >
              {savingProvider ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : providerMode === "still_deciding" ? "Clear Provider"
                : providerMode === "self_service" ? "Save as self-service"
                : "Save Provider"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ PREFERENCE CARD PREVIEW DIALOG (FULL CRUD) ═══ */}
      <Dialog open={!!prefCardPreview} onOpenChange={(v) => { if (!v) { setPrefCardPreview(null); setPrefEditMode(false); } }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="pref-card-preview-dialog">
          <div className="max-h-[80vh] overflow-y-auto">
          {prefCardPreview && (() => {
            const catPreset = getCategoryPreset(prefCardPreview.category);
            // Build category gradient dynamically from canonical categoryConfig hex colors.
            // No more hardcoded gradients per category - this guarantees Hair=#e83e8c (pink), Makeup=#9c57f5 (purple), etc.
            const accentHex = catPreset?.iconColor || "#6366F1";
            const lightHex = catPreset?.bgColor || "#e0e7ff";
            const bg = `linear-gradient(135deg, ${accentHex}, ${lightHex})`;
            const userName = currentUser ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() || "You" : "You";
            const allDefs = prefCardPreview.defaults || {};
            const relevantKeys = CATEGORY_DEFAULTS_MAP[prefCardPreview.category] || [];
            const defs = {};
            for (const [k, v] of Object.entries(allDefs)) {
              if (relevantKeys.includes(k)) defs[k] = v;
            }
            const inspoItems = prefCardPreview.inspo || [];
            const inspoImages = inspoItems.map(i => typeof i === "string" ? i : i?.image_url).filter(Boolean);
            const linkedExps = getLinkedExperiences(prefCardPreview);
            return (
              <>
                {/* Gradient header */}
                <div className="relative h-28 flex items-end p-4" style={{ background: bg }}>
                  <div className="absolute inset-0 bg-black/20" />
                  <div className="relative z-10 flex items-center gap-3 w-full">
                    <div className="w-14 h-14 rounded-full border-2 border-white shadow overflow-hidden bg-white flex-shrink-0">
                      {currentUser?.profile_photo_url ? (
                        <img src={currentUser.profile_photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <span className="text-lg font-bold text-gray-500">{userName.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-white flex-1">
                      {prefEditMode ? (
                        <Input value={prefEditName} onChange={e => setPrefEditName(e.target.value)}
                          onBlur={() => { if (prefEditName.trim() && prefEditName !== prefCardPreview.name) updateExperienceField(prefCardPreview.experience_id, "name", prefEditName); }}
                          className="h-7 text-sm bg-white/20 border-white/40 text-white placeholder:text-white/50" data-testid="pref-edit-name" />
                      ) : (
                        <p className="text-base font-semibold leading-tight">{prefCardPreview.name || catPreset?.name}</p>
                      )}
                      <p className="text-xs opacity-80">{userName}</p>
                      {prefEditMode ? (
                        <Input type="date" value={prefEditDate} onChange={e => { setPrefEditDate(e.target.value); updateExperienceField(prefCardPreview.experience_id, "date", e.target.value || null); }}
                          className="h-6 text-[10px] bg-white/20 border-white/40 text-white w-32 mt-0.5" />
                      ) : (
                        prefCardPreview.date && <p className="text-[10px] opacity-60 mt-0.5">{fmtDate(prefCardPreview.date)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => shareExperience(prefCardPreview)}
                        className="w-7 h-7 rounded-full flex items-center justify-center bg-white/20 text-white hover:bg-white/30 transition-all"
                        title="Share" data-testid="pref-share-btn">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { setPrefEditMode(!prefEditMode); setPrefEditName(prefCardPreview.name || ""); setPrefEditDate(prefCardPreview.date?.slice(0, 10) || ""); }}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${prefEditMode ? "bg-white text-gray-800" : "bg-white/20 text-white hover:bg-white/30"}`}
                        data-testid="pref-edit-toggle">
                        {prefEditMode ? <Check className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Card body */}
                <div className="p-4 space-y-3">
                  {/* Provider - tappable */}
                  <button onClick={() => openProviderModal(prefCardPreview)} className="flex items-center gap-2 w-full text-left" data-testid="pref-provider-tap">
                    {prefCardPreview.provider_name ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs gap-1"><UserCheck className="w-3 h-3" />{formatProvider(prefCardPreview.provider_name)}</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-0 text-xs gap-1 italic"><Info className="w-3 h-3" />Tap to add provider</Badge>
                    )}
                    <Pencil className="w-3 h-3 text-gray-300" />
                  </button>

                  {/* Inspo strip - with X buttons for removal + add button */}
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                      Inspiration
                      {prefEditMode && (
                        <button onClick={() => {
                          // Use the user's first available inspo not already attached
                          const existing = new Set(inspoImages);
                          const available = userInspo.filter(ui => !existing.has(ui.image_url));
                          if (available.length > 0) {
                            addInspoToExperience(prefCardPreview.experience_id, available[0]);
                          } else {
                            toast.info("Upload more inspo from the Inspiration tab first");
                          }
                        }} className="text-purple-600 hover:text-purple-800 flex items-center gap-0.5 text-[10px] font-medium normal-case tracking-normal">
                          <Plus className="w-3 h-3" />Add
                        </button>
                      )}
                    </p>
                    {inspoImages.length > 0 ? (
                      <div className="flex gap-1.5 overflow-x-auto">
                        {inspoImages.map((url, i) => (
                          <div key={i} className="relative w-14 h-14 flex-shrink-0 group">
                            <button onClick={() => openLightbox(prefCardPreview.inspo, i)} className="w-full h-full rounded-lg overflow-hidden bg-gray-100 hover:ring-2 hover:ring-purple-300 cursor-zoom-in">
                              <img src={url} alt="" className="w-full h-full object-cover" />
                            </button>
                            {prefEditMode && (
                              <button onClick={(e) => { e.stopPropagation(); removeInspoFromExperience(prefCardPreview.experience_id, i); }}
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center shadow hover:bg-red-600 z-10"
                                data-testid={`remove-inspo-${i}`}>
                                <X className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No inspo attached</p>
                    )}
                  </div>

                  {/* Defaults / Preferences - human-readable labels */}
                  {Object.keys(defs).length > 0 && (
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                        Preferences
                        <button onClick={() => openInlineDefaults(prefCardPreview.category, prefCardPreview.experience_id)}
                          className="text-purple-600 hover:text-purple-800 flex items-center gap-0.5 text-[10px] font-medium normal-case tracking-normal"
                          data-testid="pref-card-edit-defaults-btn">
                          <Pencil className="w-3 h-3" />Edit
                        </button>
                      </p>
                      <div className="space-y-2">
                        {Object.entries(defs).map(([key, val]) => {
                          const sectionLabel = getFieldLabel(key);
                          if (sectionLabel === null) return null;
                          return (
                            <div key={key} className="bg-gray-50 rounded-lg p-2">
                              <p className="text-[10px] font-bold text-gray-500 uppercase">{sectionLabel}</p>
                              {Array.isArray(val) ? (
                                <p className="text-xs text-gray-700">{humanizeValue(val)}</p>
                              ) : typeof val === "object" && val !== null ? (
                                Object.entries(val).filter(([k]) => getFieldLabel(k) !== null).slice(0, 8).map(([k, v]) => {
                                  const fLabel = getFieldLabel(k);
                                  if (!fLabel) return null;
                                  const displayVal = humanizeValue(v);
                                  if (!displayVal) return null;
                                  return (
                                    <div key={k} className="flex justify-between text-xs py-0.5">
                                      <span className="text-gray-400">{fLabel}</span>
                                      <span className="text-gray-700 text-right max-w-[60%] truncate">{displayVal}</span>
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="text-xs text-gray-600">{humanizeValue(val)}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {Object.keys(defs).length === 0 && inspoImages.length === 0 && linkedExps.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-4">No preferences or inspo attached yet</p>
                  )}

                  {/* Linked Experiences (occasion connections) */}
                  {linkedExps.length > 0 && (
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Linked Experiences</p>
                      <div className="space-y-1.5">
                        {linkedExps.map(le => {
                          const lPreset = getCategoryPreset(le.category);
                          const LIcon = lPreset?.icon || Calendar;
                          const leInspo = (le.inspo || []).map(i => typeof i === "string" ? i : i?.image_url).filter(Boolean);
                          return (
                            <button key={le.experience_id} onClick={() => setPrefCardPreview(le)}
                              className="w-full flex items-center gap-2 p-2 rounded-lg border border-gray-100 hover:border-gray-200 bg-white text-left transition-all"
                              data-testid={`linked-exp-${le.experience_id}`}>
                              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: lPreset?.bgColor || "#f3f4f6" }}>
                                <LIcon className="w-3.5 h-3.5" style={{ color: lPreset?.iconColor || "#6b7280" }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate">{le.name}</p>
                                <p className="text-[10px] text-gray-400">{formatProvider(le.provider_name) || "No provider"}{le.date ? ` · ${fmtDate(le.date)}` : ""}</p>
                              </div>
                              {leInspo.length > 0 && (
                                <div className="flex -space-x-1.5 flex-shrink-0">
                                  {leInspo.slice(0, 3).map((url, i) => (
                                    <div key={i} className="w-6 h-6 rounded-md overflow-hidden border border-white">
                                      <img src={url} alt="" className="w-full h-full object-cover" />
                                    </div>
                                  ))}
                                </div>
                              )}
                              <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExperiencesPage;
